import type { FC } from 'react'

import type { SxProps, Theme } from '@mui/material'
import { Box, Typography } from '@mui/material'

import { HOME_CONTENT } from '../defaults'

// ---------------------------------------------------------------------------
// Border colors — matching resolveSessionBorderColor in SessionNodeRenderer
// ---------------------------------------------------------------------------

const SAFE_COLOR = 'rgba(76, 175, 80, 0.3)'
const DANGEROUS_COLOR = 'rgba(244, 67, 54, 0.3)'
const VIEW_ONLY_COLOR = 'rgba(255, 193, 7, 0.3)'

const SAFE_BG = 'rgba(76, 175, 80, 0.04)'
const DANGEROUS_BG = 'rgba(244, 67, 54, 0.04)'
const VIEW_ONLY_BG = 'rgba(255, 193, 7, 0.04)'

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export const SessionModesSection: FC = () => (
  <Box component="section" sx={sectionContainerSx}>
    <Typography component="h2" sx={titleSx}>
      {HOME_CONTENT.sessionModes.title}
    </Typography>
    <Typography sx={subtitleSx}>{HOME_CONTENT.sessionModes.description}</Typography>

    <Box sx={cardsContainerSx}>
      <Box sx={buildCardSx(SAFE_COLOR, SAFE_BG)}>
        <Typography sx={cardTitleSx}>{HOME_CONTENT.sessionModes.cards[0].title}</Typography>
        <Typography sx={cardSubtitleSx}>{HOME_CONTENT.sessionModes.cards[0].body}</Typography>
      </Box>

      <Box sx={buildCardSx(DANGEROUS_COLOR, DANGEROUS_BG)}>
        <Typography sx={cardTitleSx}>{HOME_CONTENT.sessionModes.cards[1].title}</Typography>
        <Typography sx={cardSubtitleSx}>{HOME_CONTENT.sessionModes.cards[1].body}</Typography>
      </Box>

      <Box sx={buildCardSx(VIEW_ONLY_COLOR, VIEW_ONLY_BG)}>
        <Typography sx={cardTitleSx}>{HOME_CONTENT.sessionModes.cards[2].title}</Typography>
        <Typography sx={cardSubtitleSx}>{HOME_CONTENT.sessionModes.cards[2].body}</Typography>
      </Box>
    </Box>
  </Box>
)

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const sectionContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
  py: { xs: 4, md: 6 },
  px: { xs: 2, md: 4 },
}

const titleSx: SxProps<Theme> = {
  color: 'text.primary',
  fontSize: { xs: '1.5rem', md: '1.75rem' },
  fontWeight: 600,
  textAlign: 'center',
  mb: 1,
}

const subtitleSx: SxProps<Theme> = {
  color: 'text.secondary',
  fontSize: { xs: '1rem', md: '1.1rem' },
  fontWeight: 400,
  textAlign: 'center',
  mb: { xs: 4, md: 6 },
}

const cardsContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  gap: { xs: 4, md: 6 },
  width: '100%',
  maxWidth: 1200,
}

const buildCardSx = (borderColor: string, bgColor: string): SxProps<Theme> => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  flex: { xs: '1 1 100%', md: '1 1 0' },
  maxWidth: 420,
  px: { xs: 2.5, md: 4 },
  py: { xs: 3.5, md: 5 },
  borderRadius: 2,
  backgroundColor: bgColor,
  border: `1px solid ${borderColor}`,
})

const cardTitleSx: SxProps<Theme> = {
  color: 'text.primary',
  fontSize: { xs: '1.25rem', md: '1.5rem' },
  fontWeight: 600,
  mb: 0.5,
}

const cardSubtitleSx: SxProps<Theme> = {
  color: 'text.secondary',
  fontSize: '0.95rem',
  lineHeight: 1.6,
}
